import React, { useState, useEffect, useRef } from 'react';
import { useStore } from '../context/StoreContext.tsx';
import { useAuth } from '../context/AuthContext.tsx';
import { storage, auth } from '../lib/firebase.ts';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { updatePassword } from 'firebase/auth';
import { createPortal } from 'react-dom';
import { 
  User as UserIcon, 
  Building2, 
  Bell, 
  Shield, 
  CreditCard, 
  Save, 
  Database, 
  CheckCircle,
  Loader2,
  Camera,
  AlertTriangle,
  Server,
  Key,
  Users,
  UserPlus,
  MoreVertical,
  Mail,
  X,
  User,
  Clock,
  RotateCcw,
  Trash2,
  Edit2,
  ShieldAlert
} from 'lucide-react';

type SettingsTab = 'profile' | 'team' | 'workspace' | 'notifications' | 'security' | 'billing';

interface PendingInvite {
  id: string;
  name: string;
  email: string;
  sentAt: string;
}

// --- OVERLAY MENU COMPONENT ---
const ActionOverlay: React.FC<{ 
  anchorEl: HTMLElement | null; 
  onClose: () => void; 
  children: React.ReactNode;
}> = ({ anchorEl, onClose, children }) => {
  if (!anchorEl) return null;

  const rect = anchorEl.getBoundingClientRect();
  const style: React.CSSProperties = {
    position: 'fixed',
    top: `${rect.bottom + 8}px`,
    right: `${window.innerWidth - rect.right}px`,
    zIndex: 1000,
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[999]" onClick={onClose} />
      <div style={style} className="w-48 bg-[var(--bg-primary)] rounded-xl shadow-2xl border border-[var(--border-subtle)] py-1.5 animate-fadeIn">
        {children}
      </div>
    </>,
    document.body
  );
};

const Settings: React.FC = () => {
  const { user, updateUserAuthProfile } = useAuth();
  const { assets, deleteAsset, userProfile, updateUserExtendedProfile, migrateLegacyImages } = useStore();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState<string | null>(null);

  // Invitation Modal State
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  
  // Member Actions state
  const [activeMemberMenuId, setActiveMemberMenuId] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  
  // Pending Invites State
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([
    { id: 'inv-1', name: 'Elizabeth M', email: 'elizabethm@steelheadproductions.com', sentAt: new Date().toLocaleDateString() }
  ]);

  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    role: '',
    title: '',
    photoURL: ''
  });

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (user && userProfile && !isDataLoaded) {
      setProfile({
        fullName: user.displayName || '',
        email: user.email || '',
        role: userProfile.role || '',
        title: userProfile.title || '',
        photoURL: userProfile.photoURL || user.photoURL || ''
      });
      setIsDataLoaded(true);
    }
  }, [user, userProfile, isDataLoaded]);

  const handleSaveProfile = async () => {
    setIsLoading(true);
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      if (!user) throw new Error("Not authenticated");
      const updatedData: any = {
        role: profile.role,
        title: profile.title
      };
      if (pendingPhoto && storage) {
        const storageRef = ref(storage, `users/${user.uid}/avatar_${Date.now()}.jpg`);
        await uploadString(storageRef, pendingPhoto, 'data_url');
        const downloadURL = await getDownloadURL(storageRef);
        updatedData.photoURL = downloadURL;
        setProfile(prev => ({ ...prev, photoURL: downloadURL }));
      }
      await updateUserExtendedProfile(updatedData);
      if (user.displayName !== profile.fullName) {
         await updateUserAuthProfile({
            displayName: profile.fullName,
            photoURL: updatedData.photoURL || user.photoURL
         });
      }
      setPendingPhoto(null);
      setSuccessMsg('Changes saved successfully');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (error: any) {
      console.error("Profile save error:", error);
      setErrorMsg(error.message || 'Failed to save changes.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (newPassword !== confirmPassword) {
      setErrorMsg("Passwords do not match");
      return;
    }
    const currentUser = auth?.currentUser;
    if (!currentUser) {
      setErrorMsg("No active session found.");
      return;
    }
    setIsLoading(true);
    try {
      await updatePassword(currentUser, newPassword);
      setSuccessMsg("Password updated successfully");
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (error: any) {
      setErrorMsg(error.message || "Failed to update password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (ev) => {
      setPendingPhoto(ev.target?.result as string);
      setProfile(prev => ({ ...prev, photoURL: ev.target?.result as string }));
    };
  };

  const handleSendInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail || !inviteName) return;
    
    const newInvite: PendingInvite = {
      id: `inv-${Date.now()}`,
      name: inviteName,
      email: inviteEmail,
      sentAt: new Date().toLocaleDateString()
    };
    
    setPendingInvites(prev => [newInvite, ...prev]);
    setSuccessMsg(`Invitation sent to ${inviteName}`);
    setInviteEmail('');
    setInviteName('');
    setIsInviteModalOpen(false);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleRevokeInvite = (id: string) => {
    setPendingInvites(prev => prev.filter(inv => inv.id !== id));
    setSuccessMsg('Invitation revoked');
    setTimeout(() => setSuccessMsg(null), 2000);
  };

  const handleClearData = async () => {
    if (confirm('Are you sure you want to clear ALL assets?')) {
      setIsLoading(true);
      try {
        for (const asset of assets) {
          await deleteAsset(asset.id);
        }
        setSuccessMsg('All assets cleared.');
      } catch (error) {
        setErrorMsg('Failed to clear assets.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleMigrate = async () => {
     if (confirm('Migrate base64 images to cloud storage?')) {
        setIsLoading(true);
        try {
           const count = await migrateLegacyImages();
           setSuccessMsg(`Successfully migrated ${count} images.`);
        } catch (e) {
           setErrorMsg('Migration failed.');
        } finally {
           setIsLoading(false);
        }
     }
  };

  const menuItems = [
    { id: 'profile', label: 'My Profile', icon: UserIcon },
    { id: 'team', label: 'Team Management', icon: Users },
    { id: 'workspace', label: 'Workspace', icon: Building2 },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'billing', label: 'Billing & Plans', icon: CreditCard },
  ];

  return (
    <div className="max-w-6xl mx-auto h-[calc(100vh-140px)] flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-[var(--text-primary)]">Settings</h1>
        <p className="text-[var(--text-secondary)]">Manage your account and workspace preferences.</p>
      </div>

      <div className="flex flex-col md:flex-row gap-8 flex-1 min-h-0">
        <aside className="w-full md:w-64 flex-shrink-0 space-y-2 overflow-y-auto pr-2 scrollbar-hide">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id as SettingsTab);
                  setErrorMsg(null);
                  setSuccessMsg(null);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  isActive 
                    ? 'bg-[var(--bg-primary)] text-[var(--text-primary)] shadow-sm ring-1 ring-[var(--border-subtle)]' 
                    : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]'
                }`}
              >
                <Icon size={18} />
                {item.label}
              </button>
            );
          })}
        </aside>

        <main className="flex-1 bg-[var(--bg-primary)] rounded-2xl border border-[var(--border-subtle)] shadow-sm overflow-hidden flex flex-col">
          {activeTab === 'profile' && (
            <div className="flex-1 overflow-y-auto p-8 relative">
              <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">Personal Information</h2>
              <div className="flex flex-col md:flex-row items-start gap-8 mb-8">
                <div className="flex flex-col items-center gap-3">
                  <div className="relative group w-32 h-32">
                    <div className="w-32 h-32 rounded-full bg-[var(--bg-tertiary)] flex-shrink-0 overflow-hidden border border-[var(--border-default)] shadow-sm">
                        {profile.photoURL ? (
                          <img src={profile.photoURL} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[var(--accent-primary)] to-purple-600 text-white font-bold text-4xl">
                            {profile.fullName?.charAt(0).toUpperCase()}
                          </div>
                        )}
                    </div>
                    <label className="absolute inset-0 flex items-center justify-center bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                       <Camera size={24} />
                       <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                    </label>
                  </div>
                </div>
                <div className="flex-1 w-full grid grid-cols-1 md:grid-cols-2 gap-6">
                   <div>
                     <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Full Name</label>
                     <input 
                       type="text" 
                       value={profile.fullName}
                       onChange={(e) => setProfile({...profile, fullName: e.target.value})}
                       className="input-field"
                     />
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Email Address</label>
                     <input type="email" value={profile.email} disabled className="input-field opacity-60 cursor-not-allowed" />
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Role</label>
                     <input type="text" value={profile.role} onChange={(e) => setProfile({...profile, role: e.target.value})} className="input-field" placeholder="e.g. Director" />
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Title</label>
                     <input type="text" value={profile.title} onChange={(e) => setProfile({...profile, title: e.target.value})} className="input-field" placeholder="e.g. Design Lead" />
                   </div>
                </div>
              </div>

              {successMsg && (
                <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-xl border border-green-200 flex items-center gap-2 text-sm">
                   <CheckCircle size={18} /> {successMsg}
                </div>
              )}

              {errorMsg && (
                <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-xl border border-red-200 flex items-center gap-2 text-sm">
                   <AlertTriangle size={18} /> {errorMsg}
                </div>
              )}

              <div className="flex justify-end pt-6 border-t border-[var(--border-subtle)]">
                <button onClick={handleSaveProfile} disabled={isLoading} className="btn-primary flex items-center gap-2 min-w-[140px] justify-center">
                  {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                  Save Changes
                </button>
              </div>
            </div>
          )}

          {activeTab === 'team' && (
            <div className="flex-1 overflow-y-auto p-8 relative space-y-10">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-[var(--text-primary)]">Team Management</h2>
                  <p className="text-sm text-[var(--text-secondary)]">Invite colleagues and track access requests.</p>
                </div>
                <button 
                  onClick={() => setIsInviteModalOpen(true)}
                  className="btn-black px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-opacity shadow-sm"
                >
                  <UserPlus size={18} /> Invite Member
                </button>
              </div>

              {successMsg && (
                <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-xl border border-green-200 flex items-center gap-2 text-sm">
                   <CheckCircle size={18} /> {successMsg}
                </div>
              )}

              {/* Members List */}
              <div className="card border border-[var(--border-subtle)] overflow-hidden">
                <div className="p-4 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)] flex justify-between items-center">
                   <h3 className="font-bold text-[var(--text-primary)] flex items-center gap-2">
                     <Users size={18} className="text-[var(--text-tertiary)]" />
                     Team Members
                   </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-[var(--bg-primary)] border-b border-[var(--border-subtle)]">
                      <tr>
                        <th className="p-4 text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider">Member</th>
                        <th className="p-4 text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider">Joined</th>
                        <th className="p-4 text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--border-subtle)]">
                        <tr className="hover:bg-[var(--bg-secondary)] transition-colors group">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              {profile.photoURL ? (
                                <img src={profile.photoURL} className="w-10 h-10 rounded-full border border-[var(--border-subtle)] object-cover" alt="" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--bg-tertiary)] to-[var(--border-default)] flex items-center justify-center font-bold text-xs text-[var(--text-secondary)]">
                                  {profile.fullName?.split(' ').map(n => n[0]).join('') || 'U'}
                                </div>
                              )}
                              <div className="min-w-0">
                                <div className="text-sm font-bold text-[var(--text-primary)] flex items-center gap-1.5">
                                  {profile.fullName || 'You'}
                                  <span className="text-[10px] text-[var(--text-tertiary)] font-normal">(you)</span>
                                </div>
                                <div className="text-xs text-[var(--text-secondary)] truncate">{profile.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-sm text-[var(--text-secondary)]">
                            {user?.metadata.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : '1/24/2026'}
                          </td>
                          <td className="p-4 text-right relative">
                             <button 
                               onClick={(e) => { e.stopPropagation(); setMenuAnchorEl(e.currentTarget); setActiveMemberMenuId('me'); }}
                               className="p-2 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded-lg transition-all"
                             >
                                <MoreVertical size={16} />
                             </button>
                          </td>
                        </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pending Invitations Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-[var(--text-primary)] flex items-center gap-2">
                    <Clock size={18} className="text-[var(--text-tertiary)]" />
                    Pending Invitations
                  </h3>
                  <span className="text-xs text-[var(--text-tertiary)] font-bold uppercase tracking-widest">{pendingInvites.length} Pending</span>
                </div>
                
                <div className="card border border-[var(--border-subtle)] overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead className="bg-[var(--bg-primary)] border-b border-[var(--border-subtle)]">
                        <tr>
                          <th className="p-4 text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider">Recipient</th>
                          <th className="p-4 text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider">Date Sent</th>
                          <th className="p-4 text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--border-subtle)]">
                        {pendingInvites.length > 0 ? pendingInvites.map(invite => (
                          <tr key={invite.id} className="hover:bg-[var(--bg-secondary)] transition-colors group">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-[var(--bg-tertiary)] flex items-center justify-center text-[var(--text-tertiary)]">
                                  <User size={16} />
                                </div>
                                <div className="min-w-0">
                                  <div className="text-sm font-bold text-[var(--text-primary)]">{invite.name}</div>
                                  <div className="text-xs text-[var(--text-secondary)] truncate">{invite.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="p-4 text-sm text-[var(--text-secondary)]">
                              {invite.sentAt}
                            </td>
                            <td className="p-4 text-right">
                               <div className="flex justify-end gap-1">
                                 <button 
                                   onClick={() => setSuccessMsg(`Resent invitation to ${invite.name}`)}
                                   className="p-2 text-[var(--text-tertiary)] hover:text-[var(--accent-primary)] hover:bg-[var(--bg-tertiary)] rounded-lg transition-all"
                                   title="Resend Invite"
                                 >
                                    <RotateCcw size={16} />
                                 </button>
                                 <button 
                                   onClick={() => handleRevokeInvite(invite.id)}
                                   className="p-2 text-[var(--text-tertiary)] hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                   title="Revoke Invite"
                                 >
                                    <Trash2 size={16} />
                                 </button>
                               </div>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan={3} className="p-8 text-center text-[var(--text-tertiary)] text-sm italic">
                               No pending invitations at this time.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Invite Modal Overlay */}
              {isInviteModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
                  <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl w-full max-w-md border border-[var(--border-subtle)] animate-slideUp overflow-hidden">
                    <div className="p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-bold text-[var(--text-primary)] flex items-center gap-2">
                          <UserPlus size={20} className="text-[var(--accent-primary)]" />
                          Invite New Member
                        </h3>
                        <button onClick={() => setIsInviteModalOpen(false)} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
                           <X size={20} />
                        </button>
                      </div>
                      
                      <form onSubmit={handleSendInvite} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-[var(--text-primary)] mb-1.5">Full Name</label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" size={16} />
                            <input 
                              type="text" 
                              required
                              autoFocus
                              placeholder="Jane Doe"
                              className="input-field pl-10"
                              value={inviteName}
                              onChange={(e) => setInviteName(e.target.value)}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[var(--text-primary)] mb-1.5">Email Address</label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" size={16} />
                            <input 
                              type="email" 
                              required
                              placeholder="colleague@company.com"
                              className="input-field pl-10"
                              value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="bg-[var(--bg-secondary)] p-3 rounded-lg border border-[var(--border-subtle)] text-xs text-[var(--text-secondary)] italic leading-relaxed">
                           Invitations are sent via email. Team members have identical access to create, view, and manage Chip assets.
                        </div>
                        <div className="flex justify-end gap-3 pt-4 border-t border-[var(--border-subtle)]">
                          <button 
                            type="button" 
                            onClick={() => setIsInviteModalOpen(false)}
                            className="px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] rounded-lg"
                          >
                            Cancel
                          </button>
                          <button 
                            type="submit" 
                            className="btn-black py-2 px-6"
                          >
                            Send Invite
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}

              {/* Team Action Overlay */}
              <ActionOverlay anchorEl={menuAnchorEl} onClose={() => { setMenuAnchorEl(null); setActiveMemberMenuId(null); }}>
                  <button onClick={() => { setActiveTab('profile'); setMenuAnchorEl(null); }} className="w-full px-4 py-2 text-xs font-semibold hover:bg-[var(--bg-secondary)] flex items-center gap-2.5 transition-colors"><Edit2 size={14} className="text-[var(--text-tertiary)]" /> Edit Profile</button>
                  <button onClick={() => { setActiveTab('security'); setMenuAnchorEl(null); }} className="w-full px-4 py-2 text-xs font-semibold hover:bg-[var(--bg-secondary)] flex items-center gap-2.5 transition-colors"><Shield size={14} className="text-[var(--text-tertiary)]" /> Security Settings</button>
                  <div className="h-px bg-[var(--border-subtle)] my-1"></div>
                  <button className="w-full px-4 py-2 text-xs font-semibold text-red-500 hover:bg-red-50 flex items-center gap-2.5 transition-colors"><Trash2 size={14} /> Remove Access</button>
              </ActionOverlay>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="flex-1 overflow-y-auto p-8">
                <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">Security Settings</h2>
                <div className="max-w-xl">
                   <div className="bg-[var(--bg-secondary)] p-6 rounded-xl border border-[var(--border-subtle)]">
                      <h3 className="font-bold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                        <Key size={18} /> Change Password
                      </h3>
                      <div className="space-y-4">
                         <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="input-field" placeholder="New Password" />
                         <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="input-field" placeholder="Confirm Password" />
                         <button onClick={handleUpdatePassword} disabled={isLoading || !newPassword} className="btn-primary flex items-center gap-2">
                           Update Password
                         </button>
                      </div>
                   </div>
                </div>
            </div>
          )}

          {activeTab === 'workspace' && (
            <div className="flex-1 overflow-y-auto p-8 space-y-8">
              <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">Workspace Settings</h2>
              <div className="space-y-8">
                <div className="bg-[var(--bg-secondary)] p-6 rounded-xl border border-[var(--border-subtle)]">
                   <h3 className="font-bold text-[var(--text-primary)] mb-2 flex items-center gap-2">
                     <Server size={18} /> Storage Migration
                   </h3>
                   <button onClick={handleMigrate} disabled={isLoading} className="px-4 py-2 bg-[var(--bg-primary)] border border-[var(--border-default)] rounded-lg text-sm font-medium hover:bg-[var(--bg-tertiary)] flex items-center gap-2">
                     Migrate Legacy Images
                   </button>
                </div>
                <div className="bg-[var(--bg-secondary)] p-6 rounded-xl border border-[var(--border-subtle)]">
                   <h3 className="font-bold text-[var(--text-primary)] mb-2 flex items-center gap-2">
                     <Database size={18} /> Data Management
                   </h3>
                   <button onClick={handleClearData} disabled={isLoading} className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100 flex items-center gap-2">
                     Clear All Assets
                   </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Settings;